import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Fragment, type CSSProperties } from 'react';
import '../../index.scss';
import drag from '../../../../../../../assets/drag.png';
import InfoIcon from '../../../../../../../components/Icons/infoIcon';
import { FormItem } from '../../../../../../../api/types/expenseareas';

const FormItemCanvasInner = ({
    item,
    isRequired,
    withDragHandleIcon
}: {
    item: FormItem;
    isRequired: boolean;
    withDragHandleIcon?: boolean;
}) => {
    return (
        <>
            {item?.type === 'single-select' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="draggable__row">
                        <div className="single-select">
                            <div className="row">
                                {item?.label ? item?.label : 'Label name'}
                                {isRequired ? <div className="required">*</div> : ''}
                            </div>
                        </div>
                    </div>
                    {item?.description && <div className="helper-text">{item?.description}</div>}

                    <div className="single-select__wrapper">
                        {item?.options?.map((elem, index) => {
                            return (
                                <div className="single-select__option" key={index}>
                                    <div className="single-select__radio"></div>{' '}
                                    {elem?.value ? elem.value : `Option ${index + 1}`}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {item?.type === 'slider' && (
                <div>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="draggable__row">
                        <div className="slider">
                            <div className="row">
                                {item?.label ? item?.label : 'Label name'}
                                {isRequired ? <div className="required">*</div> : ''}
                            </div>
                        </div>
                    </div>

                    <div
                        className="steps"
                        style={{
                            margin: `0 calc(100% / ${Math.max(item?.options?.length ?? 1, 1)} / 6)`
                        }}
                    >
                        {item?.options?.map((elem, index) => (
                            <Fragment key={index}>
                                <div className="step">
                                    <div className="step__value">{elem.value}</div>
                                </div>
                                {item?.options?.length && index < item?.options?.length - 1 ? (
                                    <div className="line" />
                                ) : null}
                            </Fragment>
                        ))}
                    </div>
                    {item?.description && <div className="helper-text">{item?.description}</div>}
                </div>
            )}

            {item?.type === 'explanation-banner' && (
                <>
                    {withDragHandleIcon && <img src={drag} alt="" className="drag-overlay__icon" />}
                    <div className="explanation-banner" style={{ whiteSpace: 'pre-wrap' }}>
                        <InfoIcon fill="#001965" />
                        {item?.description ? item?.description : 'Please, enter explanation'}
                    </div>
                </>
            )}
        </>
    );
};

export const SortableFormItemDragOverlay = ({ item }: { item: FormItem }) => {
    const isRequired = item?.settings?.find((elem) => elem.type === 'required')?.switchValue;

    return (
        <div className="draggable drag-overlay" style={{ cursor: 'grabbing' }}>
            <FormItemCanvasInner item={item} isRequired={!!isRequired} withDragHandleIcon />
        </div>
    );
};

export const SortableFormItem = ({
    id,
    item,
    selectedElement,
    fieldsError,
    onSelect
}: {
    id: string;
    item: FormItem;
    selectedElement: null | string;
    fieldsError: string[];
    onSelect?: (id: string) => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : undefined,
        touchAction: 'none'
    };

    const isRequired = item?.settings?.find((elem) => elem.type === 'required')?.switchValue;

    const fieldHasError = fieldsError.some((fid) => String(fid) === String(id));

    return (
        <>
            <div
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                role="presentation"
                onClick={() => onSelect?.(String(item.id))}
                style={style}
                data-builder-field-id={String(id)}
                className={`draggable ${selectedElement === item.id + '' ? 'selected' : ''} ${
                    fieldHasError && selectedElement !== item.id + '' ? 'error' : ''
                }`}
                key={item.id}
            >
                <FormItemCanvasInner item={item} isRequired={!!isRequired} />
            </div>
            {fieldHasError && selectedElement !== item.id + '' && !isDragging && (
                <div className="error-text">This field is not set yet. Please set field</div>
            )}
        </>
    );
};
